import { DeepSignal, patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { AgentConversationOverview, AgentMessage } from '../../hotel-gpt/types/agent-conversation';
import { generateRandomId } from '../../shared/utils/unique-id-utils';

interface PollJob {
    conversationId: string;
    transactionId: string;
    question: string;
}

interface HotelGptState {
    conversations: AgentConversationOverview[];
    activeConversationId: string | null;
    activeMessages: AgentMessage[];
    pendingConversationIds: Set<string>;
    pollJobs: PollJob[];
    isLoading: boolean;
}

const initialState: HotelGptState = {
    conversations: [],
    activeConversationId: null,
    activeMessages: [],
    pendingConversationIds: new Set(),
    pollJobs: [],
    isLoading: false,
};

export interface HotelGptStoreType {
    conversations: DeepSignal<AgentConversationOverview[]>;
    activeConversationId: DeepSignal<string | null>;
    activeMessages: DeepSignal<AgentMessage[]>;
    pendingConversationIds: DeepSignal<Set<string>>;
    pollJobs: DeepSignal<PollJob[]>;
    isLoading: DeepSignal<boolean>;

    resetState: () => void;
    setConversations: (conversations: AgentConversationOverview[]) => void;
    setIsLoading: (isLoading: boolean) => void;
    selectConversation: (conversationId: string) => void;
    startNewChat: () => void;
    setActiveMessages: (messages: AgentMessage[]) => void;
    addActiveMessage: (message: AgentMessage) => void;
    updateActiveMessage: (transactionId: string, updates: Partial<AgentMessage>) => void;
    addPendingConversation: (conversationId: string) => void;
    removePendingConversation: (conversationId: string) => void;
    clearPendingConversations: () => void;
    addConversation: (conversation: AgentConversationOverview) => void;
    updateConversationTimestamp: (conversationId: string) => void;
    addPollJob: (conversationId: string, transactionId: string, question: string) => void;
    removePollJob: (conversationId: string) => void;
    clearPollJobs: () => void;
    removeConversation: (conversationId: string) => void;
}

export const HotelGptStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((store) => ({
        resetState(): void {
            patchState(store, initialState);
        },
        setConversations(conversations: AgentConversationOverview[]): void {
            patchState(store, { conversations });
        },
        setIsLoading(isLoading: boolean): void {
            patchState(store, { isLoading });
        },
        selectConversation(conversationId: string): void {
            patchState(store, { activeConversationId: conversationId });
        },
        startNewChat(): void {
            patchState(store, { activeConversationId: generateRandomId() });
        },
        addPendingConversation(conversationId: string): void {
            patchState(store, {
                pendingConversationIds: new Set([...store.pendingConversationIds(), conversationId]),
            });
        },
        removePendingConversation(conversationId: string): void {
            const ids = new Set(store.pendingConversationIds());
            ids.delete(conversationId);
            patchState(store, { pendingConversationIds: ids });
        },
        clearPendingConversations(): void {
            patchState(store, { pendingConversationIds: new Set() });
        },
        addConversation(conversation: AgentConversationOverview): void {
            patchState(store, { conversations: [conversation, ...store.conversations()] });
        },
        updateConversationTimestamp(conversationId: string): void {
            const now = new Date().toISOString();
            const conversations = store
                .conversations()
                .map((c) => (c.conversationId === conversationId ? { ...c, updatedAt: now } : c))
                .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
            patchState(store, { conversations });
        },
        setActiveMessages(messages: AgentMessage[]): void {
            patchState(store, { activeMessages: messages });
        },
        addActiveMessage(message: AgentMessage): void {
            patchState(store, { activeMessages: [...store.activeMessages(), message] });
        },
        updateActiveMessage(transactionId: string, updates: Partial<AgentMessage>): void {
            patchState(store, {
                activeMessages: store
                    .activeMessages()
                    .map((m) => (m.transactionId === transactionId ? { ...m, ...updates } : m)),
            });
        },
        addPollJob(conversationId: string, transactionId: string, question: string): void {
            patchState(store, { pollJobs: [...store.pollJobs(), { conversationId, transactionId, question }] });
        },
        removePollJob(conversationId: string): void {
            patchState(store, { pollJobs: store.pollJobs().filter((j) => j.conversationId !== conversationId) });
        },
        clearPollJobs(): void {
            patchState(store, { pollJobs: [] });
        },
        removeConversation(conversationId: string): void {
            patchState(store, {
                conversations: store.conversations().filter((c) => c.conversationId !== conversationId),
            });
        },
    })),
);
